import { useEffect, useState } from "react";
import { useLive, post, get } from "../lib/api";
import type { Settings, Gasto } from "../lib/api";

/** Estilo unico de campo -- usado pela tela e pela seção de custo. */
const campo =
  "w-full bg-surface-2 border border-rule-2 rounded-lg px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-accent outline-none";
import { Card, Button, Empty, Carregando, Segmentos } from "../lib/ui";
import { ConexaoGoogle } from "./AgendaGoogle";

/**
 * Ajustes que mudam o atendimento — todos a um clique.
 *
 * Antes moravam no `.env`: trocar o nome que a IA usa exigia editar arquivo e
 * reiniciar processo. Quem opera a imobiliária não faz isso, e o resultado era
 * a IA se apresentando para cliente de verdade com o nome que ficou no arquivo
 * desde a instalação.
 */
export function Ajustes() {
  const { data, reload } = useLive<Settings>("/settings", []);
  const [f, setF] = useState<Settings | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (data) setF(data);
  }, [data]);

  if (!f) return <Carregando />;

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => {
    setF({ ...f, [k]: v });
    setSalvo(false);
  };

  async function salvar() {
    setErro(null);
    const r = await post<Settings & { error?: string }>("/settings", f);
    if (r.error) {
      setErro(r.error);
      return;
    }
    setSalvo(true);
    reload();
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">Quem o cliente pensa que é</h3>
          <p className="text-xs text-ink-3 mt-0.5 leading-relaxed">
            É a primeira linha que o lead lê. Use o nome de um corretor de
            verdade — quando ele assumir a conversa, do lado do cliente é a
            mesma pessoa continuando.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <label className="space-y-1">
            <span className="text-xs text-ink-3">Nome</span>
            <input
              value={f.agentName}
              onChange={(e) => set("agentName", e.target.value)}
              className={campo}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-ink-3">Empresa</span>
            <input
              value={f.companyName}
              onChange={(e) => set("companyName", e.target.value)}
              className={campo}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-ink-3">Cidade</span>
            <input
              value={f.city}
              onChange={(e) => set("city", e.target.value)}
              className={campo}
            />
          </label>
        </div>

        <label className="space-y-1 block">
          <span className="text-xs text-ink-3">
            Política comercial — o que ela pode e não pode oferecer sozinha
            (opcional)
          </span>
          <textarea
            value={f.policy ?? ""}
            onChange={(e) => set("policy", e.target.value || null)}
            rows={3}
            placeholder="Ex: não negociar desconto; não prometer data de entrega; permuta sempre passa pelo corretor."
            className={`${campo} leading-relaxed resize-y`}
          />
        </label>
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">O que o atendimento persegue</h3>
          <p className="text-xs text-ink-3 mt-0.5 leading-relaxed">
            Muda o prompt inteiro, não uma frase. É a decisão que mais afeta o
            tamanho da conversa — e o custo.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {(
            [
              {
                v: "agendar" as const,
                t: "Marcar a conversa",
                d: "Cria relação, fala do projeto o suficiente pra dar vontade, e leva pra uma ligação, vídeo ou visita. Pergunta pouco: região, propósito, momento. Conversa curta, corretor recebe agenda cheia com contexto pronto.",
              },
              {
                v: "qualificar" as const,
                t: "Qualificar antes de passar",
                d: "Descobre entrada, parcela, renda e viabilidade antes de entregar. Conversa longa e mais cara, mas o corretor recebe menos gente e mais pronta.",
              },
            ]
          ).map((o) => (
            <button
              key={o.v}
              onClick={() => set("objetivo", o.v)}
              className={`text-left p-3 rounded-lg border transition-colors ${
                f.objetivo === o.v
                  ? "border-accent bg-accent-dim"
                  : "border-rule-2 hover:border-rule"
              }`}
            >
              <span className="text-sm text-ink block">{o.t}</span>
              <span className="text-xs text-ink-3 block mt-1 leading-relaxed">
                {o.d}
              </span>
            </button>
          ))}
        </div>

        {f.objetivo === "agendar" && (
          <label className="space-y-1 block">
            <span className="text-xs text-ink-3">
              Disponibilidade — é o que ela diz quando propõe horário
            </span>
            <input
              value={f.disponibilidade ?? ""}
              onChange={(e) => set("disponibilidade", e.target.value || null)}
              placeholder="qualquer dia e horário, inclusive fim de semana e à noite"
              className={campo}
            />
            <span className="text-[11px] text-ink-3 block">
              Vazio usa o padrão acima. Aperte aqui se a agenda apertar — ela
              nunca promete horário que você não tem.
            </span>
          </label>
        )}
      </section>

      <SecaoHorarios f={f} set={set} />

      <SecaoCusto f={f} set={set} />

      <Chave
        titulo="Atender número novo automaticamente"
        ligado={f.aceitarNovosLeads}
        onChange={(v) => set("aceitarNovosLeads", v)}
        ligadoTexto="A IA responde quem chamar pela primeira vez."
        desligadoTexto={
          "A mensagem e o contato continuam sendo gravados — não se perde " +
          "ninguém — mas a conversa nasce travada e aparece na fila esperando " +
          "uma pessoa. Conversa já em andamento continua sendo atendida."
        }
      />

      <Chave
        titulo="Oferecer unidade específica do catálogo"
        ligado={f.catalogo === "on"}
        onChange={(v) => set("catalogo", v ? "on" : "off")}
        ligadoTexto={
          "A IA busca no catálogo e oferece apartamento, preço e condição. " +
          "Exige manter status e ficha em dia — unidade vendida que ficar como " +
          "disponível vira oferta errada para o cliente."
        }
        desligadoTexto={
          "A IA fala do empreendimento pelo que está em Base de conhecimento, " +
          "qualifica, e passa pro corretor quando a conversa chega em " +
          "apartamento, preço ou disponibilidade."
        }
      />

      <div className="flex items-center gap-3 pt-1">
        <Button variant="primary" onClick={salvar}>
          Salvar
        </Button>
        {salvo && <span className="text-xs text-ok">salvo</span>}
        {erro && <span className="text-xs text-danger">{erro}</span>}
        <span className="text-xs text-ink-3 ml-auto">
          Vale na próxima mensagem. Não precisa reiniciar nada.
        </span>
      </div>

      <ConexaoGoogle />

      <SecaoBackup />
    </div>
  );
}

/** Interruptor com a consequência escrita, não só o rótulo. */
function Chave({
  titulo,
  ligado,
  onChange,
  ligadoTexto,
  desligadoTexto,
}: {
  titulo: string;
  ligado: boolean;
  onChange: (v: boolean) => void;
  ligadoTexto: string;
  desligadoTexto: string;
}) {
  return (
    <Card className="p-3" stripe={ligado ? undefined : "accent"}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={ligado}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5"
        />
        <span className="min-w-0">
          <span className="text-sm text-ink block">{titulo}</span>
          <span className="text-xs text-ink-3 block mt-1 leading-relaxed">
            {ligado ? ligadoTexto : desligadoTexto}
          </span>
        </span>
      </label>
    </Card>
  );
}

/**
 * Custo e saldo, num lugar só.
 *
 * Estes ajustes moravam no `.env` — mudar o teto de gasto ou o esforço do
 * modelo exigia editar arquivo e reiniciar processo. São exatamente os que mais
 * precisam de mão rápida: o esforço é a única alavanca que troca qualidade por
 * dinheiro, e o teto é o que faz a IA parar de responder.
 *
 * O saldo é contra o TETO, não contra o crédito da conta na Anthropic — nenhuma
 * API pública expõe crédito restante. Na prática dá no mesmo quando o teto é
 * ajustado ao que foi comprado, e tem a vantagem de ser o número que de fato
 * determina quando o atendimento para.
 */
/*
 * Os dois botões que mais mexem no custo, lado a lado.
 *
 * Os preços aqui são os de @axis/core (MODELOS); o painel não importa o
 * pacote do servidor, então repete os números. Se um mudar lá, muda aqui.
 */
const MODELOS_UI: {
  id: Settings["modelo"];
  nome: string;
  preco: string;
  diz: string;
}[] = [
  {
    id: "claude-opus-5",
    nome: "Opus 5",
    preco: "US$ 5 entrada · US$ 25 saída, por milhão",
    diz: "Mais cuidadoso com as regras sutis do atendimento e com a hora de passar para o corretor.",
  },
  {
    id: "claude-sonnet-5",
    nome: "Sonnet 5",
    preco: "US$ 2 entrada · US$ 10 saída, por milhão",
    diz: "Mais rápido e 60% mais barato em tudo, cache incluído. Teste no Simulador antes de trocar a produção.",
  },
];

const ESFORCOS: { id: Settings["effort"]; rotulo: string; diz: string }[] = [
  { id: "low", rotulo: "Baixo", diz: "Pensa pouco antes de responder. Mais barato e mais rápido; pode ficar raso em conversa que pede cuidado." },
  { id: "medium", rotulo: "Médio", diz: "Equilíbrio entre custo e cuidado. Bom para a maioria das conversas." },
  { id: "high", rotulo: "Alto", diz: "Pensa mais antes de cada resposta. Padrão dos modelos." },
  { id: "xhigh", rotulo: "Muito alto", diz: "Para quando a conversa está saindo rasa mesmo no alto. Custa mais." },
  { id: "max", rotulo: "Máximo", diz: "O máximo de raciocínio. Raramente vale num chat de WhatsApp." },
];

function ModeloEEsforco({
  f,
  set,
}: {
  f: Settings;
  set: <K extends keyof Settings>(k: K, v: Settings[K]) => void;
}) {
  const esforco = ESFORCOS.find((e) => e.id === f.effort) ?? ESFORCOS[2]!;
  return (
    <div className="space-y-4">
      <div>
        <span className="text-sm text-ink-3">Modelo</span>
        <div className="grid sm:grid-cols-2 gap-3 mt-1.5">
          {MODELOS_UI.map((m) => (
            <button
              key={m.id}
              onClick={() => set("modelo", m.id)}
              className={`text-left p-3.5 rounded-xl border transition-colors ${
                f.modelo === m.id
                  ? "border-accent bg-accent-dim"
                  : "border-rule-2 hover:border-white/20"
              }`}
            >
              <span className="text-sm font-semibold text-ink block">{m.nome}</span>
              <span className="text-xs text-accent block mt-0.5">{m.preco}</span>
              <span className="text-xs text-ink-3 block mt-2 leading-relaxed">{m.diz}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-sm text-ink-3 block mb-1.5">Esforço</span>
        <Segmentos
          atual={f.effort}
          onChange={(id) => set("effort", id)}
          opcoes={ESFORCOS.map((e) => ({ id: e.id, rotulo: e.rotulo }))}
        />
        <p className="text-xs text-ink-3 mt-2 leading-relaxed">{esforco.diz}</p>
      </div>

      <p className="text-xs text-ink-3 leading-relaxed">
        Os dois valem na próxima mensagem, sem reiniciar nada. Trocar de
        modelo ou de esforço grava o prompt de novo no cache uma vez (cerca de
        US$ 0,11 no Opus e US$ 0,04 no Sonnet) — por isso vale decidir e
        deixar, não ficar alternando.
      </p>
    </div>
  );
}

function SecaoCusto({
  f,
  set,
}: {
  f: Settings;
  set: <K extends keyof Settings>(k: K, v: Settings[K]) => void;
}) {
  const { data: gasto } = useLive<Gasto>("/gasto", ["agent_turns"]);
  const usd = (n: number) => `US$ ${n.toFixed(2)}`;

  const acabando =
    gasto?.restante !== null &&
    gasto !== null &&
    gasto.restante !== undefined &&
    gasto.restante <= f.avisarSaldoUsd;

  return (
    <section className="space-y-3">
      <h2 className="text-sm text-ink">Custo do modelo</h2>

      {gasto && (
        <Card className="p-3" stripe={acabando ? "danger" : undefined}>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <span className="text-ink-3">
              gasto total <span className="text-ink">{usd(gasto.total)}</span>
            </span>
            <span className="text-ink-3">
              hoje <span className="text-ink">{usd(gasto.hoje)}</span>
            </span>
            {gasto.restante !== null && (
              <span className="text-ink-3">
                restam{" "}
                <span className={acabando ? "text-danger" : "text-ok"}>
                  {usd(gasto.restante)}
                </span>
              </span>
            )}
          </div>
          {gasto.restante === null && (
            <p className="text-xs text-ink-3 mt-2">
              Sem teto configurado — a IA responde sem limite de gasto.
            </p>
          )}
          {acabando && (
            <p className="text-xs text-danger mt-2">
              No teto a IA para de responder e as conversas passam para o
              corretor. Recarregue no Console da Anthropic e suba o teto aqui.
            </p>
          )}
        </Card>
      )}

      <ModeloEEsforco f={f} set={set} />

      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-sm text-ink-3">Teto total (US$)</span>
          <input
            type="number"
            min={0}
            step="1"
            className={campo}
            value={f.tetoTotalUsd}
            onChange={(e) => set("tetoTotalUsd", Number(e.target.value))}
          />
          <span className="text-[11px] text-ink-3 block">
            0 desliga o limite.
          </span>
        </label>
        <label className="block">
          <span className="text-sm text-ink-3">Teto por dia (US$)</span>
          <input
            type="number"
            min={0}
            step="1"
            className={campo}
            value={f.tetoDiarioUsd}
            onChange={(e) => set("tetoDiarioUsd", Number(e.target.value))}
          />
          <span className="text-[11px] text-ink-3 block">
            Protege contra um dia fora da curva.
          </span>
        </label>
        <label className="block">
          <span className="text-sm text-ink-3">Avisar quando restar (US$)</span>
          <input
            type="number"
            min={0}
            step="1"
            className={campo}
            value={f.avisarSaldoUsd}
            onChange={(e) => set("avisarSaldoUsd", Number(e.target.value))}
          />
          <span className="text-[11px] text-ink-3 block">
            Vai no WhatsApp de quem marcar <em>Saldo do modelo acabando</em> em
            Avisos.
          </span>
        </label>
      </div>

      <label className="block">
        <span className="text-sm text-ink-3">Transcrição de áudio</span>
        <select
          className={campo}
          value={f.transcricaoProvedor}
          onChange={(e) =>
            set(
              "transcricaoProvedor",
              e.target.value as Settings["transcricaoProvedor"],
            )
          }
        >
          <option value="nenhum">desligada</option>
          <option value="groq">Groq (tem plano gratuito)</option>
          <option value="openai">OpenAI</option>
        </select>
        <span className="text-[11px] text-ink-3 block">
          A chave do provedor continua no servidor. Sem chave, a nota de voz é
          guardada mesmo assim e o agente responde sem ter ouvido.
        </span>
      </label>
    </section>
  );
}

/**
 * As duas janelas de horário.
 *
 * São duas porque o risco é diferente. O expediente é a hora em que uma
 * PESSOA atende do outro lado: vale para follow-up e para os horários que a IA
 * oferece, porque marcar ligação para as 21h30 é marcar para ninguém.
 *
 * A abordagem estica mais. Quem preencheu um formulário às 21h está com o
 * assunto na mão, e responder rápido vale mais que esperar a manhã seguinte —
 * desde que não vire mensagem de madrugada, que é o que faz a pessoa bloquear
 * em vez de responder.
 *
 * Antes isso vivia em três lugares no código e ninguém podia mudar.
 */
function SecaoHorarios({
  f,
  set,
}: {
  f: Settings;
  set: <K extends keyof Settings>(k: K, v: Settings[K]) => void;
}) {
  const hora = (n: number) => `${String(n).padStart(2, "0")}:00`;

  const Faixa = ({
    rotulo,
    de,
    ate,
    ajuda,
  }: {
    rotulo: string;
    de: keyof Settings;
    ate: keyof Settings;
    ajuda: string;
  }) => (
    <div>
      <span className="text-sm text-ink-3">{rotulo}</span>
      <div className="flex items-center gap-2 mt-1">
        <select
          className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink"
          value={f[de] as number}
          onChange={(e) => set(de, Number(e.target.value) as Settings[typeof de])}
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>
              {hora(h)}
            </option>
          ))}
        </select>
        <span className="text-ink-3 text-sm">às</span>
        <select
          className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink"
          value={f[ate] as number}
          onChange={(e) => set(ate, Number(e.target.value) as Settings[typeof ate])}
        >
          {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>
              {hora(h)}
            </option>
          ))}
        </select>
      </div>
      <span className="text-[11px] text-ink-3 block mt-1">{ajuda}</span>
    </div>
  );

  return (
    <section className="space-y-3">
      <h2 className="text-sm text-ink">Horários</h2>

      <div className="grid sm:grid-cols-2 gap-4">
        <Faixa
          rotulo="Expediente"
          de="expedienteDe"
          ate="expedienteAte"
          ajuda="Follow-up e horários que a IA oferece. É quando alguém atende."
        />
        <Faixa
          rotulo="Abordagem de formulário"
          de="abordagemDe"
          ate="abordagemAte"
          ajuda="Primeira mensagem para quem preencheu. Pode ir mais tarde."
        />
      </div>

      <p className="text-xs text-ink-3 max-w-prose">
        Fora da janela nada se perde: a mensagem espera a próxima abertura.
        Domingo começa às 14h em qualquer caso — ligação comercial às 9h de
        domingo incomoda mesmo quando o expediente permite.
      </p>
    </section>
  );
}

interface Backup {
  nome: string;
  bytes: number;
  em: string;
}

/**
 * O backup do banco, baixável em um clique.
 *
 * Existe porque backup que ninguém consegue pegar não é backup. O dump vive num
 * volume Docker na VPS: tirá-lo de lá exige SSH, `docker run` com o volume
 * montado e um `tar`. Quem opera a imobiliária não faz isso — e o momento em
 * que se precisa do backup é o pior possível para aprender.
 *
 * Não há botão de apagar, de propósito.
 */
function SecaoBackup() {
  const [lista, setLista] = useState<Backup[] | null>(null);

  useEffect(() => {
    get<Backup[]>("/backups")
      .then(setLista)
      .catch(() => setLista([]));
  }, []);

  if (!lista) return null;

  const mb = (b: number) => `${(b / 1024 / 1024).toFixed(1)} MB`;

  return (
    <Card className="p-4">
      <div className="text-sm text-ink">Backup do banco</div>

      {lista.length === 0 ? (
        <p className="text-sm text-ink-3 mt-2 max-w-prose">
          Nenhum backup ainda. Ele é gerado uma vez por dia; o primeiro sai
          logo depois do deploy. Se continuar vazio amanhã, o serviço de backup
          não está subindo.
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-3 mt-1">
            Um por dia, os últimos sete. Guarda tudo: leads, conversas, base de
            conhecimento e configurações.
          </p>
          <div className="mt-3 space-y-1">
            {lista.map((b) => (
              <div key={b.nome} className="flex items-center gap-3 text-sm">
                <a
                  className="text-accent hover:underline"
                  href={`/api/backups/${b.nome}`}
                >
                  {b.nome}
                </a>
                <span className="text-ink-3 text-xs">{mb(b.bytes)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-ink-3 mt-3 max-w-prose">
            Vale baixar um de tempos em tempos e guardar fora da VPS. Backup no
            mesmo servidor protege contra engano; não protege contra perder o
            servidor.
          </p>
        </>
      )}
    </Card>
  );
}
